/**
 * UoM & conversions admin — `/catalog/uoms` (SPEC screen 4). Factors are
 * NUMERIC(20,10) decimal STRINGS end to end — never a JS number; entries are
 * canonicalized to exactly ten fractional digits by string padding before
 * send. Conversions have a composite {from_uom_id, to_uom_id} key (no id).
 * The `?from=&to=` deep link (target of the `uom_unconvertible` typed error
 * in transactional screens) pre-fills the add-conversion form with the pair.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { formatQuantity } from "@/lib/format";
import { localizedName } from "@/lib/i18n";

import {
  createUom,
  createUomConversion,
  deleteUom,
  deleteUomConversion,
  getUom,
  updateUom,
  type LifecycleStatus,
  type Uom,
  type UomCreateInput,
  type UomPatch,
} from "../api";
import { catalogKeys, useActiveUoms, useUomConversions, useUoms } from "../queries";
import { toConversionFactorString } from "../variant-schema";

/** UoM factor wire scale: NUMERIC(20,10) per the ratified contract. */
const FACTOR_FRACTION_DIGITS = 10;
const UOM_STATUSES = ["active", "archived"] as const satisfies readonly LifecycleStatus[];

interface UomDraft {
  code: string;
  name: string;
  symbol: string;
  dimension: string;
  status: LifecycleStatus;
}

const EMPTY_UOM: UomDraft = {
  code: "",
  name: "",
  symbol: "",
  dimension: "",
  status: "active",
};

export interface UomsPageProps {
  /** Deep-link pre-fill (`?from=&to=`) from a `uom_unconvertible` problem. */
  initialFrom?: string;
  initialTo?: string;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

export function UomsPage({
  initialFrom,
  initialTo,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}: UomsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const uomsQuery = useUoms();
  const activeUomsQuery = useActiveUoms(canUpdate);
  const conversionsQuery = useUomConversions();
  const uoms = uomsQuery.data ?? [];
  const activeUoms = activeUomsQuery.data ?? [];
  const conversions = conversionsQuery.data ?? [];

  const [fromUom, setFromUom] = useState(initialFrom ?? "");
  const [toUom, setToUom] = useState(initialTo ?? "");
  const [factor, setFactor] = useState("");
  const [factorError, setFactorError] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);
  const [deleteBlocker, setDeleteBlocker] = useState<string | null>(null);
  const [uomDraft, setUomDraft] = useState<UomDraft>(EMPTY_UOM);
  const [editingUomId, setEditingUomId] = useState<string | null>(null);
  const [editingUomEtag, setEditingUomEtag] = useState<string | null>(null);
  const [uomFormError, setUomFormError] = useState<string | null>(null);

  // Late-arriving deep link (route search parsed after mount in tests).
  useEffect(() => {
    if (initialFrom !== undefined) setFromUom(initialFrom);
    if (initialTo !== undefined) setToUom(initialTo);
  }, [initialFrom, initialTo]);

  // An archive invalidates the shared UoM key prefix. Once the active-only
  // refetch resolves, discard any now-terminal choice instead of sending it.
  useEffect(() => {
    if (activeUomsQuery.data === undefined) return;
    if (fromUom !== "" && !activeUomsQuery.data.some((uom) => uom.id === fromUom)) setFromUom("");
    if (toUom !== "" && !activeUomsQuery.data.some((uom) => uom.id === toUom)) setToUom("");
  }, [activeUomsQuery.data, fromUom, toUom]);

  const uomById = (id: string): Uom | undefined => uoms.find((uom) => uom.id === id);
  const codeOf = (id: string): string => uomById(id)?.code ?? id;

  const resetUomForm = () => {
    setEditingUomId(null);
    setEditingUomEtag(null);
    setUomDraft(EMPTY_UOM);
    setUomFormError(null);
  };

  const loadUomMutation = useMutation({
    mutationFn: getUom,
    onSuccess: ({ data, etag }) => {
      setEditingUomId(data.id);
      setEditingUomEtag(etag);
      setUomDraft({
        code: data.code,
        name: data.name,
        symbol: data.symbol,
        dimension: data.dimension,
        status: data.status,
      });
      setUomFormError(null);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const createUomMutation = useIdempotentMutation<Uom, UomCreateInput>({
    mutationFn: (input, key) => createUom(input, key),
    onSuccess: () => {
      resetUomForm();
      void queryClient.invalidateQueries({ queryKey: catalogKeys.uoms.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const updateUomMutation = useMutation({
    mutationFn: (input: { uomId: string; patch: UomPatch; ifMatch: string }) =>
      updateUom(input.uomId, input.patch, input.ifMatch),
    onSuccess: ({ data, etag }) => {
      if (data.status === "archived") {
        resetUomForm();
        void queryClient.invalidateQueries({ queryKey: catalogKeys.uoms.all });
        return;
      }
      setEditingUomEtag(etag);
      setUomDraft({
        code: data.code,
        name: data.name,
        symbol: data.symbol,
        dimension: data.dimension,
        status: data.status,
      });
      setUomFormError(null);
      void queryClient.invalidateQueries({ queryKey: catalogKeys.uoms.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const archiveUomMutation = useMutation({
    // List responses deliberately carry no per-row validator. Read the exact
    // entity immediately before archive and forward that resource-bound ETag;
    // never synthesize one from timestamps or list state.
    mutationFn: async (uomId: string) => {
      const { etag } = await getUom(uomId);
      if (etag === null) {
        throw new ApiError("Missing UoM ETag; reload and retry", 428);
      }
      return deleteUom(uomId, etag);
    },
    onSuccess: (_data, deletedId) => {
      if (editingUomId === deletedId) resetUomForm();
      void queryClient.invalidateQueries({ queryKey: catalogKeys.uoms.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submitUom = () => {
    if (
      uomDraft.code.trim() === "" ||
      uomDraft.name.trim() === "" ||
      uomDraft.symbol.trim() === "" ||
      uomDraft.dimension.trim() === ""
    ) {
      setUomFormError(t("catalog.uoms.unitRequired", "Complete every unit field."));
      return;
    }
    setUomFormError(null);
    if (editingUomId === null) {
      createUomMutation.mutate({
        code: uomDraft.code,
        name: uomDraft.name,
        symbol: uomDraft.symbol,
        dimension: uomDraft.dimension,
      });
      return;
    }
    if (editingUomEtag === null) {
      setUomFormError(t("catalog.uoms.unitReload", "Reload the unit before saving."));
      return;
    }
    updateUomMutation.mutate({
      uomId: editingUomId,
      ifMatch: editingUomEtag,
      patch: {
        name: uomDraft.name,
        symbol: uomDraft.symbol,
        dimension: uomDraft.dimension,
        status: uomDraft.status,
      },
    });
  };

  const addMutation = useIdempotentMutation({
    mutationFn: (input: { from_uom_id: string; to_uom_id: string; factor: string }, key: string) =>
      createUomConversion(input, key),
    onSuccess: () => {
      setFactor("");
      setDuplicateError(false);
      void queryClient.invalidateQueries({ queryKey: catalogKeys.uoms.all });
    },
    onError: (error) => {
      // Defensive: not a declared conversions-POST response, but harmless to
      // surface inline should a server ever answer with it.
      if (error instanceof ApiError && error.code === "duplicate_conversion") {
        setDuplicateError(true);
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (input: { fromUomId: string; toUomId: string }) =>
      deleteUomConversion(input.fromUomId, input.toUomId),
    onSuccess: () => {
      setDeleteBlocker(null);
      void queryClient.invalidateQueries({ queryKey: catalogKeys.uoms.all });
    },
    onError: (error: unknown) => {
      // Defensive fallback on `detail` only — no undeclared extension members.
      if (error instanceof ApiError && error.status === 422) {
        setDeleteBlocker(
          error.problem?.detail ??
            t("catalog.uoms.conversionInUse", "This conversion is in use and cannot be deleted."),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const submit = () => {
    if (
      fromUom === "" ||
      toUom === "" ||
      !activeUoms.some((uom) => uom.id === fromUom) ||
      !activeUoms.some((uom) => uom.id === toUom)
    )
      return;
    // Canonicalize to the CatalogConversionFactorString wire scale (exactly
    // ten fractional digits, nonzero) by string padding — never via Number().
    const wireFactor = toConversionFactorString(factor);
    if (wireFactor === null) {
      setFactorError(true);
      return;
    }
    setFactorError(false);
    addMutation.mutate({ from_uom_id: fromUom, to_uom_id: toUom, factor: wireFactor });
  };

  const renderFactor = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, FACTOR_FRACTION_DIGITS);
    } catch {
      return value;
    }
  };

  const previewFactor = toConversionFactorString(factor);
  const previewReady =
    fromUom !== "" &&
    toUom !== "" &&
    activeUoms.some((uom) => uom.id === fromUom) &&
    activeUoms.some((uom) => uom.id === toUom) &&
    previewFactor !== null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">{t("catalog.uoms.title", "Units of measure")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("catalog.uoms.subtitle", "Define units before creating products.")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3">
            <CardTitle>
              <h2>{t("catalog.uoms.unitsTitle", "Units")}</h2>
            </CardTitle>
            {canCreate ? (
              <Button type="button" variant="outline" size="sm" onClick={resetUomForm}>
                <Plus className="size-4" aria-hidden="true" />
                {t("catalog.uoms.newUnit", "New unit")}
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {uomsQuery.isError ? (
              <p role="alert" className="text-sm text-muted-foreground">
                {t("catalog.uoms.loadError", "Units could not be loaded.")}
              </p>
            ) : uoms.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("catalog.uoms.empty", "No units yet — define units before creating products.")}
              </p>
            ) : (
              <ul className="flex flex-col divide-y">
                {uoms.map((uom) => (
                  <li key={uom.id} className="flex items-center gap-3 py-2">
                    <span dir="ltr" className="w-16 font-mono font-medium">
                      {uom.code}
                    </span>
                    <span className="min-w-0 flex-1 text-sm text-muted-foreground">
                      {localizedName(uom.name, uom.name_ar, i18n.language)}
                    </span>
                    <Badge variant={uom.status === "archived" ? "secondary" : "outline"}>
                      {t(`catalog.uoms.unitStatus.${uom.status}`, uom.status)}
                    </Badge>
                    {canUpdate && uom.status !== "archived" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("catalog.uoms.editUnit", "Edit {{code}}", { code: uom.code })}
                        disabled={loadUomMutation.isPending}
                        onClick={() => loadUomMutation.mutate(uom.id)}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                    {canDelete && uom.status !== "archived" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("catalog.uoms.archiveUnit", "Archive {{code}}", {
                          code: uom.code,
                        })}
                        disabled={archiveUomMutation.isPending}
                        onClick={() => archiveUomMutation.mutate(uom.id)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}

            {canCreate || editingUomId !== null ? (
              <div className="grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="uom-code">{t("catalog.uoms.code", "Code")}</Label>
                  <Input
                    id="uom-code"
                    dir="ltr"
                    value={uomDraft.code}
                    disabled={editingUomId !== null}
                    onChange={(event) =>
                      setUomDraft((current) => ({ ...current, code: event.target.value }))
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="uom-name">{t("catalog.uoms.name", "Name")}</Label>
                  <Input
                    id="uom-name"
                    value={uomDraft.name}
                    onChange={(event) =>
                      setUomDraft((current) => ({ ...current, name: event.target.value }))
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="uom-symbol">{t("catalog.uoms.symbol", "Symbol")}</Label>
                  <Input
                    id="uom-symbol"
                    dir="ltr"
                    value={uomDraft.symbol}
                    onChange={(event) =>
                      setUomDraft((current) => ({ ...current, symbol: event.target.value }))
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="uom-dimension">{t("catalog.uoms.dimension", "Dimension")}</Label>
                  <Input
                    id="uom-dimension"
                    dir="ltr"
                    value={uomDraft.dimension}
                    onChange={(event) =>
                      setUomDraft((current) => ({ ...current, dimension: event.target.value }))
                    }
                  />
                </div>
                {editingUomId !== null ? (
                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <Label htmlFor="uom-status">{t("catalog.uoms.status", "Status")}</Label>
                    <Select
                      value={uomDraft.status}
                      onValueChange={(status) =>
                        setUomDraft((current) => ({
                          ...current,
                          status: status as LifecycleStatus,
                        }))
                      }
                    >
                      <SelectTrigger id="uom-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UOM_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {t(`catalog.uoms.unitStatus.${status}`, status)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                  <Button
                    type="button"
                    loading={createUomMutation.isPending || updateUomMutation.isPending}
                    onClick={submitUom}
                  >
                    {editingUomId === null
                      ? t("catalog.uoms.createUnit", "Create unit")
                      : t("catalog.uoms.saveUnit", "Save unit")}
                  </Button>
                  {editingUomId !== null ? (
                    <Button type="button" variant="ghost" onClick={resetUomForm}>
                      {t("actions.cancel")}
                    </Button>
                  ) : null}
                </div>
                {uomFormError !== null ? (
                  <p role="alert" className="text-sm text-destructive sm:col-span-2">
                    {uomFormError}
                  </p>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("catalog.uoms.conversionsTitle", "Conversions")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {conversionsQuery.isError ? (
              <p role="alert" className="text-sm text-muted-foreground">
                {t("catalog.uoms.conversionsLoadError", "Conversions could not be loaded.")}
              </p>
            ) : conversions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("catalog.uoms.conversionsEmpty", "No conversions defined yet.")}
              </p>
            ) : (
              <ul className="flex flex-col divide-y">
                {conversions.map((conversion) => (
                  <li
                    key={`${conversion.from_uom_id}/${conversion.to_uom_id}`}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <span className="flex items-center gap-2 text-sm">
                      <span dir="ltr" className="font-mono">
                        {codeOf(conversion.from_uom_id)}
                      </span>
                      <ArrowRight className="size-3 rtl:-scale-x-100" aria-hidden="true" />
                      <span dir="ltr" className="font-mono">
                        {codeOf(conversion.to_uom_id)}
                      </span>
                      <span className="text-muted-foreground">
                        {t("catalog.uoms.previewLine", "1 {{from}} = {{factor}} {{to}}", {
                          from: codeOf(conversion.from_uom_id),
                          factor: renderFactor(conversion.factor),
                          to: codeOf(conversion.to_uom_id),
                        })}
                      </span>
                    </span>
                    {canUpdate ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("catalog.uoms.deleteConversion", "Delete conversion")}
                        disabled={deleteMutation.isPending}
                        onClick={() =>
                          deleteMutation.mutate({
                            fromUomId: conversion.from_uom_id,
                            toUomId: conversion.to_uom_id,
                          })
                        }
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}

            {deleteBlocker !== null ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive"
              >
                {deleteBlocker}
              </p>
            ) : null}

            {canUpdate ? (
              <div className="flex flex-wrap items-end gap-3 border-t pt-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="uoms-from">{t("catalog.uoms.from", "From")}</Label>
                  {/* key: remount on deep-link value swap (Radix "" emission). */}
                  <Select key={fromUom} value={fromUom} onValueChange={setFromUom}>
                    <SelectTrigger id="uoms-from" className="w-28">
                      <SelectValue placeholder={t("catalog.uoms.pick", "Pick…")} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeUoms.map((uom) => (
                        <SelectItem key={uom.id} value={uom.id}>
                          {uom.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="uoms-to">{t("catalog.uoms.to", "To")}</Label>
                  <Select key={toUom} value={toUom} onValueChange={setToUom}>
                    <SelectTrigger id="uoms-to" className="w-28">
                      <SelectValue placeholder={t("catalog.uoms.pick", "Pick…")} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeUoms.map((uom) => (
                        <SelectItem key={uom.id} value={uom.id}>
                          {uom.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="uoms-factor">{t("catalog.uoms.factor", "Factor")}</Label>
                  <Input
                    id="uoms-factor"
                    dir="ltr"
                    inputMode="decimal"
                    className="w-40"
                    value={factor}
                    onChange={(event) => {
                      setFactor(event.target.value);
                      setFactorError(false);
                      setDuplicateError(false);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  onClick={submit}
                  disabled={
                    fromUom === "" || toUom === "" || factor === "" || addMutation.isPending
                  }
                  loading={addMutation.isPending}
                >
                  {t("catalog.uoms.addConversion", "Add conversion")}
                </Button>
              </div>
            ) : null}

            {previewReady ? (
              <p className="text-sm text-muted-foreground" data-testid="conversion-preview">
                {t("catalog.uoms.previewLine", "1 {{from}} = {{factor}} {{to}}", {
                  from: codeOf(fromUom),
                  factor: renderFactor(previewFactor),
                  to: codeOf(toUom),
                })}
              </p>
            ) : null}

            {factorError ? (
              <p role="alert" className="text-sm text-destructive">
                {t(
                  "catalog.uoms.invalidFactor",
                  "Enter a factor above zero with at most 10 decimal places.",
                )}
              </p>
            ) : null}
            {duplicateError ? (
              <p role="alert" className="text-sm text-destructive">
                {t("catalog.uoms.duplicateConversion", "This conversion already exists.")}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
